import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import Tabs, { TabPanel } from '@atlaskit/tabs';

import { teamNavigationConfig } from '../../jsm-portal-playground-data';
import { jsmPortalPlaygroundMessages as sharedMessages } from '../../jsm-portal-playground-messages';
import { useJsmPlaygroundAnalytics } from '../../use-jsm-playground-analytics';
import { DEMO_CURSOR_FADE_MS, JsmPlaygroundDemoCursor } from '../jsm-playground-demo-cursor';
import { centerOf, type DemoPoint, rectWithin } from '../jsm-playground-demo-geometry';
import { useJsmPlaygroundDemoLayer } from '../jsm-playground-demo-layer';
import { useNotifyDemoComplete } from '../jsm-playground-demo-timeline';
import { usePrefersReducedMotion } from '../jsm-playground-motion';
import { JsmPlaygroundScreen } from '../jsm-playground-screen';
import { JsmPlaygroundSkipAnimationButton } from '../jsm-playground-skip-animation-button';

import { SURVEY_DEMO, SURVEY_DEMO_CURSOR_LABEL, SURVEY_DEMO_RESPONSE_ID } from './survey-demo';
import { SURVEY_INSIGHTS_TRIGGER_TEST_ID } from './survey-insights';
import { SURVEY_RESPONSES_TAB_TEST_ID, SurveyPageHeader } from './survey-page-header';
import { SurveyResponses } from './survey-responses';
import {
	SURVEY_SHOW_TABLE_TEST_ID,
	SurveySummary,
	type SurveyQuestionView,
} from './survey-summary';

const SUMMARY_TAB = 0;
const RESPONSES_TAB = 1;

const shouldShowHrSkip = (
	isActive: boolean,
	isAnimationComplete: boolean,
	prefersReducedMotion: boolean,
) => isActive && !isAnimationComplete && !prefersReducedMotion;

type CursorTarget = 'start' | 'tableToggle' | 'insights' | 'responsesTab' | 'firstRowAction';

const ORIGIN: DemoPoint = { x: 0, y: 0 };

export type JsmPortalPlaygroundHrPreviewProps = {
	/** The scripted demo only plays while the tab is showing. */
	isActive?: boolean;
	/** Set once the visitor clicks inside the preview: the demo stops and hands the screen over. */
	hasUserTakenOver?: boolean;
	onDemoComplete?: () => void;
};

/**
 * The HR team's JSM Surveys page for its onboarding survey: a Summary of the answers, with a chart
 * that toggles to a table and a Rovo summary, and the individual Responses, each of which opens in a
 * panel beside the table.
 *
 * Tab panels unmount when left, so each tab replays its entrance when it is shown again. The
 * chart/table choice outlives that, so it is kept here, as is the open response, which the demo
 * drives.
 */
const JsmPortalPlaygroundHrPreviewComponent = ({
	isActive = true,
	hasUserTakenOver = false,
	onDemoComplete,
}: JsmPortalPlaygroundHrPreviewProps) => {
	const intl = useIntl();
	const prefersReducedMotion = usePrefersReducedMotion();
	const notifyDemoComplete = useNotifyDemoComplete(onDemoComplete);
	const rootRef = useRef<HTMLDivElement>(null);
	const demoLayer = useJsmPlaygroundDemoLayer();
	const demoLayerRef = useRef(demoLayer);
	demoLayerRef.current = demoLayer;
	const [hasLocalTakenOver, setHasLocalTakenOver] = useState(false);
	const isTakenOver = hasUserTakenOver || hasLocalTakenOver;
	const isTakenOverRef = useRef(isTakenOver);
	isTakenOverRef.current = isTakenOver;
	const timersRef = useRef<number[]>([]);

	const handleTakeover = useCallback(() => {
		setHasLocalTakenOver(true);
		setCursor((previous) => ({ ...previous, isVisible: false }));
		timersRef.current.forEach(window.clearTimeout);
		timersRef.current = [];
	}, []);

	useEffect(() => {
		if (hasUserTakenOver) {
			setCursor((previous) => ({ ...previous, isVisible: false }));
			timersRef.current.forEach(window.clearTimeout);
			timersRef.current = [];
		}
	}, [hasUserTakenOver]);

	useEffect(() => {
		const root = rootRef.current;
		if (!root || isTakenOver) {
			return;
		}

		root.addEventListener('focus', handleTakeover, true);
		root.addEventListener('keydown', handleTakeover);
		root.addEventListener('pointerdown', handleTakeover);

		return () => {
			root.removeEventListener('focus', handleTakeover, true);
			root.removeEventListener('keydown', handleTakeover);
			root.removeEventListener('pointerdown', handleTakeover);
		};
	}, [handleTakeover, isTakenOver]);

	const [isAnimationComplete, setIsAnimationComplete] = useState(false);
	const shouldFocusCompletedDemo = useRef(false);
	const [selectedTab, setSelectedTab] = useState(SUMMARY_TAB);
	const [questionOneView, setQuestionOneView] = useState<SurveyQuestionView>('chart');
	const [isInsightsOpen, setIsInsightsOpen] = useState(false);
	const [isInsightsOpenedByDemo, setIsInsightsOpenedByDemo] = useState(false);
	const [openResponseId, setOpenResponseId] = useState<string | null>(null);
	const [panelResponseId, setPanelResponseId] = useState<string | null>(null);
	// A scripted cursor never fires `:hover`, so the row it visits reveals its action by state.
	const [revealedResponseId, setRevealedResponseId] = useState<string | null>(null);
	const [cursor, setCursor] = useState({
		target: 'start' as CursorTarget,
		isVisible: false,
		isPressed: false,
	});
	const [cursorStops, setCursorStops] = useState<Record<CursorTarget, DemoPoint>>({
		start: ORIGIN,
		tableToggle: ORIGIN,
		insights: ORIGIN,
		responsesTab: ORIGIN,
		firstRowAction: ORIGIN,
	});
	// Off for the first paint, so the measured start lands without travelling there, and off again
	// for each silent reposition.
	const [isCursorTransitionEnabled, setIsCursorTransitionEnabled] = useState(false);
	const { firePlaygroundScreenNavigated } = useJsmPlaygroundAnalytics('hr');

	const selectTab = (index: number) => {
		// The Responses tab unmounts when left, so its open panel goes with it.
		setIsInsightsOpen(false);
		setOpenResponseId(null);
		setRevealedResponseId(null);
		setSelectedTab(index);
		firePlaygroundScreenNavigated({
			team_type: 'hr',
			screen_id: index === RESPONSES_TAB ? 'survey_responses' : 'survey_summary',
			interaction_mode: 'manual_click',
		});
	};

	const changeInsightsOpen = (isOpen: boolean) => {
		setIsInsightsOpenedByDemo(false);
		setIsInsightsOpen(isOpen);
	};

	const changeOpenResponse = (responseId: string | null) => {
		setOpenResponseId(responseId);
		if (responseId) {
			setPanelResponseId(responseId);
		}
	};

	const measureStops = useCallback((targets: CursorTarget[]) => {
		const root = rootRef.current;
		// Stops are in the coordinates of wherever the cursor renders.
		const origin = demoLayerRef.current ?? root;
		if (!root || !origin) {
			return;
		}
		const selectors: Record<Exclude<CursorTarget, 'start'>, string> = {
			tableToggle: `[data-testid="${SURVEY_SHOW_TABLE_TEST_ID}"]`,
			insights: `[data-testid="${SURVEY_INSIGHTS_TRIGGER_TEST_ID}"]`,
			responsesTab: `[data-testid="${SURVEY_RESPONSES_TAB_TEST_ID}"]`,
			firstRowAction: `[data-response-id="${SURVEY_DEMO_RESPONSE_ID}"] button`,
		};
		const next: Partial<Record<CursorTarget, DemoPoint>> = {};

		for (const target of targets) {
			const element = root.querySelector(selectors[target === 'start' ? 'tableToggle' : target]);
			if (!element) {
				continue;
			}
			const center = centerOf(rectWithin(origin, element));
			// A short approach from below-left of the toggle, rather than flying in from a corner.
			next[target] = target === 'start' ? { x: center.x - 70, y: center.y + 70 } : center;
		}

		setCursorStops((previous) => ({ ...previous, ...next }));
	}, []);

	// Re-run once the layer exists: it mounts after this screen does.
	useLayoutEffect(() => {
		measureStops(['start', 'tableToggle', 'insights', 'responsesTab']);
	}, [measureStops, demoLayer]);

	useEffect(() => {
		setIsCursorTransitionEnabled(true);
	}, []);

	useEffect(() => {
		if (!isActive) {
			return;
		}
		if (prefersReducedMotion) {
			setIsAnimationComplete(true);
			notifyDemoComplete();
		}
	}, [isActive, notifyDemoComplete, prefersReducedMotion]);

	useEffect(() => {
		if (!isActive || prefersReducedMotion || isTakenOver || isAnimationComplete) {
			return;
		}

		timersRef.current = [];
		const at = (ms: number, run: () => void) => {
			timersRef.current.push(
				window.setTimeout(() => {
					if (!isTakenOverRef.current) {
						run();
					}
				}, ms),
			);
		};
		const updateCursor = (next: Partial<typeof cursor>) =>
			setCursor((previous) => ({ ...previous, ...next }));
		const jumpTo = (target: CursorTarget) => {
			setIsCursorTransitionEnabled(false);
			updateCursor({ target });
		};
		const fadeBackIn = () => {
			setIsCursorTransitionEnabled(true);
			updateCursor({ isVisible: true });
		};

		// Fades in already travelling onto the toggle, rather than appearing and then setting off.
		at(SURVEY_DEMO.cursorIn, () => updateCursor({ target: 'tableToggle', isVisible: true }));
		at(SURVEY_DEMO.togglePress, () => updateCursor({ isPressed: true }));
		at(SURVEY_DEMO.toggleRelease, () => {
			setQuestionOneView('table');
			updateCursor({ isPressed: false });
		});
		at(SURVEY_DEMO.insightsMove, () => {
			// Re-measured: the table has just replaced the chart below the header.
			measureStops(['insights']);
			updateCursor({ target: 'insights' });
		});
		at(SURVEY_DEMO.insightsPress, () => updateCursor({ isPressed: true }));
		at(SURVEY_DEMO.insightsRelease, () => {
			setIsInsightsOpenedByDemo(true);
			setIsInsightsOpen(true);
			updateCursor({ isPressed: false });
		});
		at(SURVEY_DEMO.insightsCursorOut, () => updateCursor({ isVisible: false }));
		at(SURVEY_DEMO.responsesJump, () => jumpTo('responsesTab'));
		at(SURVEY_DEMO.responsesIn, fadeBackIn);
		at(SURVEY_DEMO.responsesPress, () => updateCursor({ isPressed: true }));
		at(SURVEY_DEMO.responsesRelease, () => {
			selectTab(RESPONSES_TAB);
			updateCursor({ isPressed: false });
		});
		at(SURVEY_DEMO.responsesCursorOut, () => updateCursor({ isVisible: false }));
		at(SURVEY_DEMO.firstRowJump, () => {
			// The Responses table only exists now, so its row is measured now.
			setRevealedResponseId(SURVEY_DEMO_RESPONSE_ID);
			measureStops(['firstRowAction']);
			jumpTo('firstRowAction');
		});
		at(SURVEY_DEMO.firstRowIn, fadeBackIn);
		at(SURVEY_DEMO.firstRowPress, () => updateCursor({ isPressed: true }));
		at(SURVEY_DEMO.firstRowRelease, () => {
			changeOpenResponse(SURVEY_DEMO_RESPONSE_ID);
			updateCursor({ isPressed: false });
		});
		// Travel stays on: easing across to where the squeeze left the button is the point.
		at(SURVEY_DEMO.firstRowReflow, () => measureStops(['firstRowAction']));
		at(SURVEY_DEMO.cursorOut, () => {
			updateCursor({ isVisible: false });
			setRevealedResponseId(null);
		});
		at(SURVEY_DEMO.cursorOut + DEMO_CURSOR_FADE_MS, () => {
			setIsAnimationComplete(true);
			notifyDemoComplete();
		});

		return () => {
			timersRef.current.forEach(window.clearTimeout);
			timersRef.current = [];
		};
		// Scheduled once per activation. `selectTab` and `changeOpenResponse` only use setters.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isActive, isAnimationComplete, prefersReducedMotion, measureStops, isTakenOver]);

	const skipAnimation = () => {
		timersRef.current.forEach(window.clearTimeout);
		timersRef.current = [];
		setCursor((previous) => ({ ...previous, isVisible: false, isPressed: false }));
		setQuestionOneView('table');
		setIsInsightsOpenedByDemo(false);
		setIsInsightsOpen(false);
		setRevealedResponseId(null);
		setSelectedTab(RESPONSES_TAB);
		setOpenResponseId(SURVEY_DEMO_RESPONSE_ID);
		setPanelResponseId(SURVEY_DEMO_RESPONSE_ID);
		setIsAnimationComplete(true);
		shouldFocusCompletedDemo.current = true;
		notifyDemoComplete();
	};

	useLayoutEffect(() => {
		if (!shouldFocusCompletedDemo.current) {
			return;
		}
		const responseButton = rootRef.current?.querySelector<HTMLButtonElement>(
			`[data-response-id="${SURVEY_DEMO_RESPONSE_ID}"] button`,
		);
		if (!responseButton) {
			return;
		}
		shouldFocusCompletedDemo.current = false;
		responseButton.focus();
	}, [openResponseId, selectedTab]);

	return (
		<div ref={rootRef} css={rootStyles}>
			<JsmPlaygroundScreen
				config={teamNavigationConfig.hr}
				teamName={intl.formatMessage(sharedMessages.teamHr)}
			>
				<Tabs
					id="jsm-playground-hr-survey"
					selected={selectedTab}
					onChange={selectTab}
					shouldUnmountTabPanelOnChange
				>
					<SurveyPageHeader />
					<TabPanel>
						<SurveySummary
							questionOneView={questionOneView}
							onQuestionOneViewChange={setQuestionOneView}
							isInsightsOpen={isInsightsOpen}
							onInsightsOpenChange={changeInsightsOpen}
							shouldFocusInsightsOnOpen={!isInsightsOpenedByDemo}
						/>
					</TabPanel>
					<TabPanel>
						<SurveyResponses
							openResponseId={openResponseId}
							panelResponseId={panelResponseId}
							onOpenResponseChange={changeOpenResponse}
							revealedResponseId={revealedResponseId}
						/>
					</TabPanel>
				</Tabs>
			</JsmPlaygroundScreen>

			<JsmPlaygroundSkipAnimationButton
				isVisible={shouldShowHrSkip(isActive, isAnimationComplete, prefersReducedMotion)}
				label={intl.formatMessage(sharedMessages.skipAnimation)}
				onClick={skipAnimation}
				placement="start"
			/>

			<JsmPlaygroundDemoCursor
				label={SURVEY_DEMO_CURSOR_LABEL}
				testId="jsm-hr-demo-cursor"
				container={demoLayer}
				hasUserTakenOver={isTakenOver}
				isPressed={cursor.isPressed}
				isTransitionEnabled={isCursorTransitionEnabled}
				isVisible={cursor.isVisible && isActive && !isTakenOver}
				x={cursorStops[cursor.target].x}
				y={cursorStops[cursor.target].y}
			/>
		</div>
	);
};

// Memoised so the playground root's cursor state changes (enter, hover, press, leave) do not
// re-render this subtree. Its props are primitives plus a stable callback.
export const JsmPortalPlaygroundHrPreview = memo(JsmPortalPlaygroundHrPreviewComponent);

const rootStyles = css({
	position: 'relative',
	width: '100%',
	height: '100%',
	// v13 tab panels pad 8px inline. The summary already pads `space.400`, same as the title,
	// so that extra inset makes the body narrower than "Onboarding survey".
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& div[role="tabpanel"]': {
		paddingInline: 0,
	},
});
